import { AlertIconKey, OpsAlertCard, PageSpinner } from '@armhr/ui';
import { Box, Grid, Typography } from '@mui/material';
import { addDays, format, formatISO, parseISO } from 'date-fns';
import { Helmet } from 'react-helmet';

import BenefitsCard from '../../components/dashboard/BenefitsCard';
import BenefitsEnrollmentCard from '../../components/dashboard/BenefitsEnrollmentCard';
import CalendarCard from '../../components/dashboard/CalendarCard';
import ClockCard from '../../components/dashboard/ClockCard';
import CompanyTeamSection from '../../components/dashboard/CompanyTeamSection';
import LaborNoticeCard from '../../components/dashboard/LaborNoticeCard';
import PayHistoryCard from '../../components/dashboard/PayHistoryCard';
import ProductNotificationsBanner from '../../components/dashboard/ProductNotificationsBanner';
import QuickActionsCard from '../../components/dashboard/QuickActionsCard';
import SwipeclockPtoCard from '../../components/dashboard/SwipeclockPtoCard';
import TimeOffCardV2 from '../../components/dashboard/TimeOffCard';
import TimecardCard from '../../components/dashboard/TimecardCard';
import W2ReminderCard from '../../components/dashboard/W2ReminderCard';
import W2ReminderModal from '../../components/dashboard/W2ReminderModal';
import W4ReminderCard from '../../components/dashboard/W4ReminderCard';
import { useCompany } from '../../contexts/company.context';
import { useApiData } from '../../hooks/useApiData';
import useFetchHolidays, {
  getHolidayLabel,
} from '../../hooks/useFetchHolidays';
import { usePtoLabel } from '../../hooks/usePtoLabel';
import { useUser } from '../../hooks/useUser';
import {
  calculateUpcomingAnniversaries,
  calculateUpcomingBirthdays,
} from '../../utils/anniversary';
import { ConfigFlags } from '../../utils/constants';
import { getDisplayName } from '../../utils/profile';

const DashboardPage: React.FC = () => {
  const { user, entity } = useUser();
  const { config: companyConfig } = useCompany();
  const { getPtoEventLabel } = usePtoLabel();
  const { data: dashboardData, loading } = useApiData<DashboardData>((api) =>
    api.profiles.getDashboard()
  );

  const { holidays } = useFetchHolidays();
  const { data: employees } = useApiData((api) => api.company.getEmployees());
  const { data: ptoData } = useApiData((api) =>
    api.benefits.getPtoRequestsSummary()
  );

  const { data: birthdayPermissions } = useApiData<BirthdayPermission[]>(
    (api) => api.company.getBirthdays()
  );

  const { data: alerts } = useApiData<AppAlert[]>((api) =>
    api.admin.getActiveAlerts()
  );

  const showBirthdays =
    companyConfig?.find((c) => c.flag === ConfigFlags.SHOW_CALENDAR_BIRTHDAYS)
      ?.value ?? false;

  const hideAnniversaries =
    companyConfig?.find((c) => c.flag === ConfigFlags.HIDE_ANNIVERSARIES)
      ?.value ?? false;

  // Build events for the calendar card
  let calendarEvents: CalendarEvent[] = [];
  if (employees && holidays && ptoData) {
    // Birthdays
    const birthdayEvents = showBirthdays
      ? calculateUpcomingBirthdays(employees, birthdayPermissions, true)
      : [];

    // Anniversaries
    const anniversaryEvents = hideAnniversaries
      ? []
      : calculateUpcomingAnniversaries(employees, true);
    // Holidays
    const holidayEvents = holidays.map((holiday) => ({
      title: getHolidayLabel(holiday),
      start: formatISO(parseISO(holiday.start_date)),
      end: formatISO(parseISO(holiday.end_date)),
      allDay: true,
      type: 'holiday',
      classNames: ['holiday-event'],
    }));
    // PTO
    const ptoEvents = (ptoData?.pto_requests || [])
      .filter(
        (request) =>
          request.status === 'A' ||
          request.status === 'P' ||
          request.status === 'N'
      )
      .filter((request) => {
        const endDate = parseISO(request.end);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return endDate >= today; // Only show requests that end today or in the future
      })
      .map((request) => {
        const endDate = parseISO(request.end);
        const nextDay = addDays(endDate, 1);
        const employee = employees.find(
          (emp) => emp.id === request.employee_id
        );
        const displayName = employee
          ? `${employee.nickname || employee.first_name} ${employee.last_name}`
          : request.name;
        return {
          title: `${displayName} ${getPtoEventLabel(request.leave_type)}`,
          start: format(parseISO(request.start), 'yyyy-MM-dd'),
          end: format(nextDay, 'yyyy-MM-dd'),
          allDay: true,
          name: displayName,
          type: 'pto',
          classNames: ['pto-event'],
        };
      });
    calendarEvents = [
      ...birthdayEvents,
      ...anniversaryEvents,
      ...holidayEvents,
      ...ptoEvents,
    ];
  }

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | Armhr</title>
        <meta
          name="description"
          content="View your Armhr dashboard with quick access to payroll, benefits, time off, and company information."
        />
      </Helmet>
      <ProductNotificationsBanner />
      <W2ReminderModal />

      {/* Greeting/Header */}
      <Box>
        <Typography variant="h2" fontWeight={700} color="text.primary">
          Welcome, {getDisplayName(user!, false)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
      </Box>

      <LaborNoticeCard />

      <Box sx={{ mt: 4 }}>
        {user?.is_swipeclock_enabled && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ width: { xs: '100%', md: '30%' } }}>
              <ClockCard />
            </Box>
            <Box sx={{ width: { xs: '100%', md: '70%' } }}>
              <SwipeclockPtoCard />
            </Box>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {alerts?.map((alert) => (
                <OpsAlertCard
                  key={alert.id}
                  title={alert.title}
                  content={alert.content}
                  icon={alert.config?.icon as AlertIconKey}
                  color={alert.config?.color}
                />
              ))}
              <BenefitsEnrollmentCard />
              <W2ReminderCard />
              <W4ReminderCard />
              <PayHistoryCard
                history={
                  dashboardData?.most_recent_voucher
                    ? [dashboardData.most_recent_voucher]
                    : []
                }
              />
              <BenefitsCard
                benefits={dashboardData?.active_benefit_plans || []}
              />
              <CompanyTeamSection
                companyTeam={dashboardData?.company_team}
                armhrContacts={dashboardData?.support || []}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <QuickActionsCard />

              {user?.is_armhr_pto_enabled &&
                user?.active_benefits?.pto_plans && (
                  <TimeOffCardV2
                    ptoSummary={dashboardData?.pto_summary || []}
                    ptoRequests={ptoData?.pto_requests || []}
                  />
                )}
              {entity?.employee_status_code === 'A' && (
                <CalendarCard events={calendarEvents} />
              )}
            </Box>
          </Grid>
        </Grid>
        {user?.is_swipeclock_enabled && (
          <Box sx={{ mt: 2 }}>
            <TimecardCard />
          </Box>
        )}
      </Box>
    </>
  );
};

export default DashboardPage;
